import React, { useState } from 'react';
import { X, Loader2, CheckCircle, XCircle, CreditCard, Shield, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { SubscriptionPlan } from '../../types/subscription';
import { SUBSCRIPTION_PLANS } from '../../constants/subscriptionPlans';
import { subscriptionService } from '../../services/subscriptionService';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlanId: string;
  onSuccess: (subscription: any) => void;
  onError: (error: string) => void;
  showTrialOption?: boolean;
}

type PaymentStep = 'plan' | 'payment' | 'processing' | 'success' | 'error';

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  selectedPlanId,
  onSuccess,
  onError,
  showTrialOption = false
}) => {
  const { language, isRTL, strings } = useLocalization();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState<PaymentStep>('plan');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);

  const selectedPlan = SUBSCRIPTION_PLANS.find(plan => plan.id === selectedPlanId);

  if (!isOpen || !selectedPlan) return null;
  
  // Determine if this subscription should be a trial
  const shouldUseTrial = showTrialOption && selectedPlan.trialDays && selectedPlan.trialDays > 0;

  const handleSubscribe = async () => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Skip payment step for free plans
      if (selectedPlan.isFreeTier) {
        setCurrentStep('processing');
        await createFreeSubscription();
      } else {
        // For paid plans, navigate to payment page
        console.log('🔄 SubscriptionModal: Navigating to payment page for plan:', selectedPlanId);
        onClose(); // Close the modal
        // Navigate to payment page with the selected plan
        window.location.href = `/payment/${selectedPlanId}`;
      }

    } catch (error) {
      console.error('Error creating subscription:', error);
      setError(error instanceof Error ? error.message : 'Failed to create subscription');
      setCurrentStep('error');
      onError(error instanceof Error ? error.message : 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const createPaidSubscription = async () => {
    const now = Date.now();
    const userRegistrationDate = new Date();
    
    // Check if this should be an extended trial
    const { TrialExtensionUtils } = require('../../constants/trialExtension');
    const shouldGetExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(userRegistrationDate);
    
    let trialEndDate: Date;
    let subscriptionEndDate: Date;
    
    if (shouldUseTrial) {
      if (shouldGetExtendedTrial) {
        trialEndDate = TrialExtensionUtils.getExtendedTrialEndDate();
        // For trial subscriptions, the subscription end date is the same as trial end date
        subscriptionEndDate = trialEndDate;
      } else {
        trialEndDate = new Date(now + selectedPlan.trialDays! * 24 * 60 * 60 * 1000);
        // For trial subscriptions, the subscription end date is the same as trial end date
        subscriptionEndDate = trialEndDate;
      }
    } else {
      // For paid subscriptions, calculate end date based on billing cycle
      const billingCycle = selectedPlan.billingCycle === 'yearly' ? 'yearly' : 'monthly';
      subscriptionEndDate = TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(now), billingCycle);
      trialEndDate = subscriptionEndDate; // Not used for paid subscriptions
    }

    const subscriptionData = {
      userId: user!.id,
      userName: user!.name,
      userPhone: user!.phoneNumber,
      planId: selectedPlan.id,
      status: 'pending' as const,
      startDate: now.toString(),
      endDate: subscriptionEndDate.getTime().toString(),
      totalPrice: selectedPlan.price,
      currency: selectedPlan.currency,
      createdBy: user!.id,
      updatedBy: user!.id,
      trialExtensionReason: shouldGetExtendedTrial ? 'business_extension' as const : 'standard_trial' as const
    };

    return await subscriptionService.createSubscription(subscriptionData);
  };



  const createFreeSubscription = async () => {
    const now = Date.now();
    const userRegistrationDate = new Date();
    
    // Check if this should be an extended trial
    const { TrialExtensionUtils } = require('../../constants/trialExtension');
    const shouldGetExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(userRegistrationDate);
    
    let trialEndDate: Date;
    let subscriptionEndDate: Date;
    
    if (shouldUseTrial) {
      if (shouldGetExtendedTrial) {
        trialEndDate = TrialExtensionUtils.getExtendedTrialEndDate();
        subscriptionEndDate = trialEndDate;
      } else {
        trialEndDate = new Date(now + (selectedPlan.trialDays || 0) * 24 * 60 * 60 * 1000);
        subscriptionEndDate = trialEndDate;
      }
    } else {
      // For free subscriptions, calculate end date based on billing cycle
      const billingCycle = selectedPlan.billingCycle === 'yearly' ? 'yearly' : 'monthly';
      subscriptionEndDate = TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(now), billingCycle);
      trialEndDate = subscriptionEndDate; // Not used for free subscriptions
    }
    
    const subscriptionData = {
      userId: user!.id,
      userName: user!.name,
      userPhone: user!.phoneNumber,
      planId: selectedPlan.id,
      status: shouldUseTrial ? 'trial' as const : 'active' as const,
      startDate: now.toString(),
      endDate: subscriptionEndDate.getTime().toString(),
      totalPrice: selectedPlan.price,
      currency: selectedPlan.currency,
      createdAt: now.toString(),
      updatedAt: now.toString(),
      createdBy: user!.id,
      updatedBy: user!.id,
      trialExtensionReason: shouldUseTrial ? (shouldGetExtendedTrial ? 'business_extension' as const : 'standard_trial' as const) : undefined
    };

    const subscription = await subscriptionService.createSubscription(subscriptionData);
    setSubscription(subscription);
    setCurrentStep('success');
    onSuccess(subscription);
  };

  const getStepIcon = (step: PaymentStep) => {
    switch (step) {
      case 'plan':
        return <CreditCard className="w-5 h-5" />;
      case 'payment':
        return <Shield className="w-5 h-5" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const getStepTitle = (step: PaymentStep) => {
    switch (step) {
      case 'plan':
        return language === 'ar' ? 'اختر خطة الاشتراك' : 'Choose Subscription Plan';
      case 'payment':
        return language === 'ar' ? 'إتمام الدفع' : 'Complete Payment';
      case 'processing':
        return language === 'ar' ? 'جاري المعالجة...' : 'Processing...';
      case 'success':
        return language === 'ar' ? 'تم الاشتراك بنجاح!' : 'Subscription Successful!';
      case 'error':
        return language === 'ar' ? 'حدث خطأ' : 'Error Occurred';
      default:
        return '';
    }
  };

  const getStepDescription = (step: PaymentStep) => {
    switch (step) {
      case 'plan':
        return language === 'ar' 
          ? 'اختر الخطة المناسبة لاحتياجاتك'
          : 'Select the plan that best fits your needs';
      case 'payment':
        return language === 'ar' 
          ? 'ادفع بأمان مع كاشير'
          : 'Pay securely with Kashier';
      case 'processing':
        return language === 'ar' 
          ? 'جاري إنشاء اشتراكك...'
          : 'Creating your subscription...';
      case 'success':
        return language === 'ar' 
          ? 'تم تفعيل اشتراكك بنجاح!'
          : 'Your subscription has been activated successfully!';
      case 'error':
        return language === 'ar' 
          ? 'حدث خطأ أثناء معالجة طلبك'
          : 'An error occurred while processing your request';
      default:
        return '';
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-primary to-accent text-white p-6 rounded-t-2xl">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                {getStepIcon(currentStep)}
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {getStepTitle(currentStep)}
                </h2>
                <p className="text-lg opacity-90">
                  {getStepDescription(currentStep)}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {currentStep === 'plan' && (
              <div className="space-y-6">
                {/* Plan Details */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      {language === 'ar' ? selectedPlan.nameAr : selectedPlan.name}
                    </h3>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-accent">
                        {selectedPlan.price} {selectedPlan.currency}
                      </div>
                      <div className="text-sm text-gray-600">
                        {language === 'ar' ? 'شهرياً' : 'per month'}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    {language === 'ar' ? selectedPlan.descriptionAr : selectedPlan.description}
                  </p>

                  {shouldUseTrial && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-900">
                          {language === 'ar' ? 'تجربة مجانية' : 'Free Trial'}
                        </span>
                      </div>
                      <p className="text-sm text-blue-800 mt-1">
                        {language === 'ar' 
                          ? `احصل على ${selectedPlan.trialDays} يوم تجربة مجانية`
                          : `Get ${selectedPlan.trialDays} days free trial`
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* Plan Features */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">
                    {language === 'ar' ? 'المميزات المتاحة' : 'Available Features'}
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {(language === 'ar' ? selectedPlan.featuresAr : selectedPlan.features).map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subscription Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {language === 'ar' ? 'تفاصيل الاشتراك' : 'Subscription Details'}
                  </h4>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {language === 'ar' ? 'تاريخ البداية' : 'Start Date'}
                      </span>
                      <span className="font-medium">
                        {new Date().toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {language === 'ar' ? 'تاريخ الانتهاء' : 'End Date'}
                      </span>
                      <span className="font-medium">
                        {(() => {
                          const { TrialExtensionUtils } = require('../../constants/trialExtension');
                          const billingCycle = selectedPlan.billingCycle === 'yearly' ? 'yearly' : 'monthly';
                          const endDate = TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(), billingCycle);
                          return endDate.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US');
                        })()}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount'}
                      </span>
                      <span className="font-bold text-accent">
                        {selectedPlan.price} {selectedPlan.currency}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <button
                    onClick={onClose}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </button>
                  
                  <button
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="flex-1 bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : selectedPlan.isFreeTier ? (
                      language === 'ar' ? 'تفعيل مجاني' : 'Activate Free'
                    ) : (
                      language === 'ar' ? 'تأكيد والدفع' : 'Confirm & Pay'
                    )}
                  </button>
                </div>
              </div>
            )}

            {currentStep === 'processing' && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-16 h-16 animate-spin text-accent mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {language === 'ar' ? 'جاري المعالجة...' : 'Processing...'}
                </h3>
                <p className="text-gray-600 text-center">
                  {language === 'ar' 
                    ? 'جاري إنشاء اشتراكك، يرجى الانتظار...'
                    : 'Creating your subscription, please wait...'
                  }
                </p>
              </div>
            )}

            {currentStep === 'success' && (
              <div className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {language === 'ar' ? 'تم الاشتراك بنجاح!' : 'Subscription Successful!'}
                </h3>
                <p className="text-lg text-gray-600 text-center mb-6">
                  {language === 'ar' 
                    ? 'تم تفعيل اشتراكك بنجاح. يمكنك الآن الاستمتاع بجميع المميزات.'
                    : 'Your subscription has been activated successfully. You can now enjoy all features.'
                  }
                </p>
                <button
                  onClick={onClose}
                  className="bg-accent text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary transition-colors"
                >
                  {language === 'ar' ? 'متابعة' : 'Continue'}
                </button>
              </div>
            )}

            {currentStep === 'error' && (
              <div className="flex flex-col items-center justify-center py-12">
                <XCircle className="w-16 h-16 text-red-500 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {language === 'ar' ? 'حدث خطأ' : 'Error Occurred'}
                </h3>
                <p className="text-lg text-gray-600 text-center mb-6">
                  {error || (language === 'ar' 
                    ? 'حدث خطأ أثناء معالجة طلبك. يرجى المحاولة مرة أخرى.'
                    : 'An error occurred while processing your request. Please try again.'
                  )}
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={onClose}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                  <button
                    onClick={() => setCurrentStep('plan')}
                    className="bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary transition-colors"
                  >
                    {language === 'ar' ? 'المحاولة مرة أخرى' : 'Try Again'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


    </>
  );
};

export default SubscriptionModal;
